import { message } from "antd";
import axios from "axios"
import i18n from "./i18n.js";
import { clearAuthStorage, getStoredCompanyIdentifier, getTenantLoginPath } from "./utils/companyIdentifier.js";

// Refresh token API base URL from environment variable
const REFRESH_TOKEN_API = process.env.REACT_APP_REFRESH_TOKEN_API;

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
// Queue of failed requests to retry after token refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Refresh the access token and store it, returning the new value.
 *
 * Exported because the agent chat stream uses `fetch`, not axios, so the
 * response interceptor below never sees its 401. Both paths must refresh the
 * same way: the backend rotates single-use (increaseTokenExpiry deletes the old
 * token), so two independent refresh implementations would race and log the
 * user out.
 */
export const refreshAccessToken = async () => {
  const { token, expires } = await refreshToken();
  localStorage.setItem("accessToken", token);
  if (expires) {
    localStorage.setItem("tokenExpires", expires);
  }
  return token;
};

// Function to refresh the token using REACT_APP_REFRESH_TOKEN_API
const refreshToken = async () => {
  const currentToken = localStorage.getItem("accessToken");
  if (!currentToken) {
    message.error(i18n.t("No refresh token available"));
  }

  try {
    // The request interceptor already attaches the access token in headers.
    const response = await axios.post(
      `${REFRESH_TOKEN_API}/refresh-token`,
      {},
      {
        headers: {
          'Content-Type': 'application/json'
        },
        // Skip interceptors to avoid infinite loop
        skipAuthRefresh: true
      }
    );

    if (response.data && response.data.token) {
      return {
        token: response.data.token,
        expires: response.data.expires
      };
    }

    throw new Error("Invalid refresh token response");
  } catch (error) {
    // If refresh token API returns 401, it means the refresh token itself has expired
    if (error?.response?.status === 401) {
      // throw new Error("Session expired");
      message.error(i18n.t("Session expired"));

    }
    throw error;
  }
};

// Function to handle logout
const handleLogout = () => {
  message.destroy();
  message.error(i18n.t("Session expired. Please login again."), 5);

  setTimeout(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const redirectFrom = urlParams.get("redirectFrom")
    const companyIdentifier = clearAuthStorage();

    if (redirectFrom && redirectFrom === "mail") {
      localStorage.setItem("redirectUrl", window.location.href)
      // return
    }
    window.location.href = getTenantLoginPath(companyIdentifier);
  }, 1500);
};

const unauthPaths = ['/login', '/forgot-password'];

axios.interceptors.request.use(req => {
  const accessToken = localStorage.getItem("accessToken");
  const userEmail = localStorage.getItem("userEmail");
  const tenantId = localStorage.getItem("tenantId");
  const companyIdentifier = getStoredCompanyIdentifier();
  const storedUserInfo = localStorage.getItem("v_userInfo");
  let profileId = localStorage.getItem("userId") || localStorage.getItem("id") || "";

  if (!profileId && storedUserInfo) {
    try {
      const parsedUserInfo = JSON.parse(storedUserInfo);
      profileId = parsedUserInfo?.userId || parsedUserInfo?.id || "";
    } catch {
      profileId = "";
    }
  }

  if (!profileId && req?.headers?.userId) {
    profileId = req.headers.userId;
  }

  // A caller may set its own Authorization (Google login sends the Google ID
  // token there, since no session exists yet), so the stored access token is
  // only a fallback and must not clobber it.
  if (!req.headers.Authorization && accessToken) {
    req.headers.Authorization = accessToken;
  }
  if (userEmail) {
    req.headers["X-User-Email"] = userEmail;
  }

  if (tenantId) {
    req.headers.tenantId = tenantId;
  }

  if (companyIdentifier) {
    req.headers["X-Identifier"] = companyIdentifier;
  }

  if (profileId) {
    req.headers["X-User-ProfileId"] = profileId;
    if (!req.headers.userId) {
      req.headers.userId = profileId;
    }
  }

  return req;
});

axios.interceptors.response.use(
  function (response) {
    // Any status code that lies within the range of 2xx cause this function to trigger
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    // Skip refresh for requests that already have skipAuthRefresh flag
    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Check if we're on an unauthenticated path
      if (unauthPaths.some(path => window.location.pathname.includes(path))) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = token;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = await refreshAccessToken();

        // Update the authorization header
        originalRequest.headers.Authorization = token;

        // Process queued requests with new token
        processQueue(null, token);

        // Retry the original request
        return axios(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        processQueue(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden - logout directly
    if (error?.response?.status === 403) {
      handleLogout();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default axios 